import React, { useEffect, useState } from "react";
import { api } from "@/planner/lib/apiClient";
import { useAuth } from "@/planner/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { catColor } from "@/planner/lib/vendy";
import { productLabel } from "@/planner/lib/format";
import { unitCost, money } from "@/planner/lib/pricing";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Minus, Plus, AlertTriangle, ScanBarcode, Trash2, Undo2 } from "lucide-react";
import WasteDialog, { wasteReason } from "@/planner/components/WasteDialog";

export default function Inventory() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "manager";
  const [data, setData] = useState({ stats: { assigned_slots: 0, low: 0, empty: 0 }, rows: [] });

  const [backstock, setBackstock] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [wasting, setWasting] = useState(null);
  const [waste, setWaste] = useState([]);
  const year = new Date().getFullYear();

  const load = async () => {
    const [{ data }, { data: prods }, { data: w }] = await Promise.all([api.get("/inventory/overview"), api.get("/products"), api.get(`/waste?year=${year}`)]);
    setWaste(w);
    setData(data);
    setBackstock(prods.filter((p) => p.on_hand > 0 || p.last_purchase || p.counted_at).sort((a, b) => productLabel(a).localeCompare(productLabel(b))));
  };

  // Correct the backstock count after counting what's on the shelf at home.
  const setOnHand = async (p, value) => {
    const n = Math.max(0, Math.round(Number(value) || 0));
    if (n === (p.on_hand || 0)) return;
    try { await api.put(`/receipts/on-hand/${p.id}`, { on_hand: n }); toast.success(`${productLabel(p)}: ${n} on hand`); load(); }
    catch (e) { toast.error("Update failed"); }
  };
  const inStock = backstock.filter((p) => p.on_hand > 0);
  const stockValue = inStock.reduce((s, p) => s + (unitCost(p) || 0) * p.on_hand, 0);
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const undoWaste = async (w) => {
    try { await api.delete(`/waste/${w.id}`); toast.success(`${w.name}: waste removed`); load(); }
    catch { toast.error("Couldn't remove it."); }
  };
  const wasteCost = waste.reduce((s, w) => s + (w.cost || 0), 0);
  const wasteUnits = waste.reduce((s, w) => s + w.units, 0);

  const adjust = async (row, delta) => {
    if (!canEdit) return;
    const next = Math.max(0, row.current + delta);
    try { await api.put(`/machines/${row.machine_id}/slot-count`, { slot_id: row.slot_id, current: next }); load(); }
    catch (e) { toast.error("Update failed"); }
  };

  return (
    <div>
      <div className="mb-5"><h1 className="font-heading text-2xl font-bold">Inventory</h1><p className="text-sm text-muted-foreground">Per-slot stock across your machines. Lowest first.</p></div>
      <div className="grid grid-cols-3 gap-4 mb-5">
        <Card className="p-4" data-testid="inv-stat-assigned"><div className="text-xs uppercase text-muted-foreground">Assigned slots</div><div className="font-heading text-2xl font-bold">{data.stats.assigned_slots}</div></Card>
        <Card className="p-4" data-testid="inv-stat-low"><div className="text-xs uppercase text-muted-foreground">Low</div><div className="font-heading text-2xl font-bold text-amber-700">{data.stats.low}</div></Card>
        <Card className="p-4" data-testid="inv-stat-empty"><div className="text-xs uppercase text-muted-foreground">Empty</div><div className="font-heading text-2xl font-bold text-red-700">{data.stats.empty}</div></Card>
      </div>

      <Card className="mb-6 p-4" data-testid="backstock">
        <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
          <div>
            <h2 className="font-heading text-lg font-semibold">Backstock</h2>
            <p className="text-xs text-muted-foreground">Bought but not yet in a machine. <Link to="/admin/planner/receipts" className="underline">Stock receipts</Link> add to it; stocking a slot takes from it.</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-sm">{inStock.reduce((s, p) => s + p.on_hand, 0)} units · <b>{money(stockValue)}</b> at cost</div>
            <Button asChild size="sm" variant="outline"><Link to="/admin/planner/count" data-testid="count-with-camera"><ScanBarcode className="mr-1.5 h-4 w-4" />Count with camera</Link></Button>
          </div>
        </div>
        {backstock.length === 0 ? <p className="text-sm text-muted-foreground">Nothing yet. Add a stock receipt, or count what you have with the camera.</p> : (
          <div className="divide-y">
            {(showAll ? backstock : inStock).map((p) => (
              <div key={p.id} className="flex items-center gap-3 py-1.5 text-sm" data-testid={`backstock-${p.id}`}>
                <span className="min-w-0 flex-1 truncate">{productLabel(p)}{p.size && <span className="text-muted-foreground"> · {p.size}</span>}</span>
                <span className="hidden text-xs text-muted-foreground sm:inline">{unitCost(p) != null ? `${money(unitCost(p))} each` : ""}{p.last_purchase ? ` · ${p.last_purchase.store} ${p.last_purchase.date}` : ""}</span>
                {canEdit
                  ? <Input key={p.on_hand} type="number" min="0" defaultValue={p.on_hand || 0} className="h-8 w-20 text-right" aria-label={`${productLabel(p)} on hand`} onBlur={(e) => setOnHand(p, e.target.value)} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} />
                  : <span className="w-20 text-right font-mono">{p.on_hand || 0}</span>}
                {canEdit && <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-muted-foreground hover:text-red-700" title="Log waste" aria-label={`Log waste of ${productLabel(p)}`} data-testid={`waste-${p.id}`} onClick={() => setWasting(p)}><Trash2 className="h-4 w-4" /></Button>}
              </div>
            ))}
          </div>
        )}
        {backstock.length > inStock.length && <button type="button" className="mt-2 text-xs underline" onClick={() => setShowAll(!showAll)}>{showAll ? "Only show what's on hand" : `Show ${backstock.length - inStock.length} bought items with none left`}</button>}
      </Card>

      {waste.length > 0 && (
        <Card className="mb-6 p-4" data-testid="waste-card">
          <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
            <div>
              <h2 className="font-heading text-lg font-semibold">Waste in {year}</h2>
              <p className="text-xs text-muted-foreground">Broken, expired or missing product. Log it with the trash button on a backstock row, or from Count Stock.</p>
            </div>
            <div className="text-sm">{wasteUnits} unit{wasteUnits === 1 ? "" : "s"} · <b>{money(wasteCost)}</b> at cost</div>
          </div>
          <div className="divide-y">
            {waste.map((w) => (
              <div key={w.id} className="flex items-center gap-3 py-1.5 text-sm" data-testid={`waste-row-${w.id}`}>
                <span className="w-20 shrink-0 text-xs text-muted-foreground">{w.date}</span>
                <span className="min-w-0 flex-1 truncate">{w.units} × {w.name}{w.note && <span className="text-muted-foreground"> · {w.note}</span>}</span>
                <span className="hidden text-xs text-muted-foreground sm:inline">{wasteReason(w.reason)}</span>
                <span className="w-16 text-right font-mono text-xs">{w.cost != null ? money(w.cost) : "—"}</span>
                {canEdit && <button type="button" className="text-muted-foreground hover:text-foreground" onClick={() => undoWaste(w)} title="Undo" aria-label={`Undo waste of ${w.name}`}><Undo2 className="h-4 w-4" /></button>}
              </div>
            ))}
          </div>
        </Card>
      )}
      <WasteDialog product={wasting} onClose={() => setWasting(null)} onSaved={load} />

      {data.rows.length === 0 ? <Card className="p-10 text-center text-muted-foreground">No assigned slots yet. Assign products to machine slots in the planogram.</Card> : (
        <div className="space-y-2">
          {data.rows.map((r) => {
            const c = catColor(r.category);
            return (
              <Card key={`${r.machine_id}-${r.slot_id}`} className="flex items-center justify-between gap-3 p-3" data-testid={`inv-row-${r.machine_id}-${r.slot_id}`} style={{ borderLeft: `5px solid ${c.stripe}` }}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2"><span className="font-mono text-xs bg-muted px-1.5 rounded">{r.slot_id}</span><span className="font-semibold truncate">{r.product_name}</span>{r.current === 0 && <Badge className="bg-red-600"><AlertTriangle className="mr-1 h-3 w-3" />Empty</Badge>}{r.low && r.current > 0 && <Badge className="bg-amber-500">Low</Badge>}</div>
                  <div className="text-xs text-muted-foreground">{r.machine_name} · need {r.deficit}</div>
                </div>
                <div className="flex items-center gap-2">
                  {canEdit && <Button variant="outline" size="icon" className="h-8 w-8" data-testid={`inv-minus-${r.machine_id}-${r.slot_id}`} onClick={() => adjust(r, -1)}><Minus className="h-4 w-4" /></Button>}
                  <span className="font-mono text-sm w-12 text-center">{r.current}/{r.par}</span>
                  {canEdit && <Button variant="outline" size="icon" className="h-8 w-8" data-testid={`inv-plus-${r.machine_id}-${r.slot_id}`} onClick={() => adjust(r, 1)}><Plus className="h-4 w-4" /></Button>}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
